/**
 * Recommendation Policy module constants (S3-01).
 * Requirement sources: Elevate PRS DRAFT 0.10.7 Appendix C (every numeric
 * default is named here and cited to its Appendix C row; rules never carry
 * bare literals), Appendix D (policy versioning), REC-08 (deadline defaults
 * per tenant type).
 */
import type { z } from 'zod';

import { LimitingFactorV1, PathwayStatusV1, TemOutcomeV1 } from '../icd/enums';

/**
 * Appendix D: on approval the policy becomes Recommendation Policy v1.0.
 * Every recommendation records the policy version that produced it (REC-02).
 */
export const POLICY_VERSION = '1.0';

/** Appendix C "Assessment window - count": 3 assessments, fixed for MVP. */
export const ASSESSMENT_WINDOW_COUNT = 3;

/** Appendix C "Assessment window - age": 365 days, fixed for MVP. */
export const ASSESSMENT_WINDOW_AGE_DAYS = 365;

/**
 * Appendix C "Mandatory trigger grades": 1 and 2, fixed for MVP (D-2, REC-03).
 * The list is unchanged by the REC-03 amendment of 4 September 2026: grade 2
 * still enters rule D-2. What the amendment changes is the OUTCOME for grade 2,
 * which now depends on the TEM trigger below. Grade 1 is unconditional.
 */
export const MANDATORY_TRIGGER_GRADES: readonly number[] = [1, 2];

/**
 * REC-03 as amended (Andre Berger, third mail of 4 September 2026, confirmed
 * 11:30 the same day): the one driving grade whose mandatory outcome depends on
 * the TEM trigger. Named rather than written as a bare 2, on the same
 * discipline as every other constant in this file.
 */
export const TEM_BRANCH_GRADE = 2;

/**
 * Limb 1 of the TEM trigger, Core Framework v4.2.5 Part 2 section 3.4.2
 * "Grade 2 remediation": remediation becomes an obligation when the grade 2
 * carries a TEM outcome of safety compromised or safety momentarily reduced.
 * The two codes are ICD section 8.3's own and are taken from the enum rather
 * than restated as strings.
 */
export const TEM_TRIGGER_OUTCOMES: readonly z.infer<typeof TemOutcomeV1>[] = [
  TemOutcomeV1.enum.safety_compromised,
  TemOutcomeV1.enum.safety_momentarily_reduced,
];

/**
 * Limb 2 of the same trigger: or when the TEM outcome is the limiting factor
 * for that grade. One of the three ICD section 8.4 codes, from the enum.
 */
export const TEM_TRIGGER_LIMITING_FACTOR: z.infer<typeof LimitingFactorV1> =
  LimitingFactorV1.enum.tem_outcome;

/** Appendix D rules D-3 and D-3b: driving grades that trigger on cited evidence. */
export const OPTIONAL_TRIGGER_GRADE = 3;
export const REFINEMENT_TRIGGER_GRADES: readonly number[] = [4, 5];

/**
 * Appendix C "Maximum not-yet-started optional recommendations (rule D-9)":
 * default 3, permitted range 1-10, operator-administrator tunable per tenant.
 */
export const OPTIONAL_CAP_DEFAULT = 3;

/*
 * Appendix C "Maximum supplement objects per pathway" (rule D-10) is
 * WITHDRAWN in DRAFT 0.10.7 (DEC-33, section 17.6): no maximum applies and
 * every supplement object mapped to a cited observable behaviour attaches.
 * SUPPLEMENT_CAP_DEFAULT and its config override are therefore removed
 * rather than defaulted, because a configuration knob for a withdrawn rule
 * is dead code. The D-9 optional-recommendation cap above is a different
 * cap and stands.
 */

/**
 * Appendix C "Airline deadline default": 3 months (REC-08). Calendar-month
 * arithmetic, clamped to the last day of a shorter month, is applied by the
 * wiring that materialises the date; the policy emits the period only.
 */
export const AIRLINE_DEADLINE_DEFAULT = 'P3M';

/** Appendix C "ATO deadline default": 1 week (REC-08). */
export const ATO_DEADLINE_DEFAULT = 'P1W';

/**
 * Appendix C: "A pathway deadline runs from the moment when the pathway
 * enters Mandatory recommendation status." The policy is pure and computes
 * no dates; it emits deadline inputs with this anchor for the wiring.
 */
export const DEADLINE_RUNS_FROM = 'mandatory_recommendation_entry';

/** Appendix H / rule D-3c: the trainee-visible label of a content gap. */
export const CONTENT_GAP_TRAINEE_LABEL = 'Content in preparation';

type PathwayStatus = z.infer<typeof PathwayStatusV1>;

/**
 * Appendix A: terminal pathway statuses. Rule D-8 suppresses duplicates
 * against any NON-terminal ("open") pathway for the same competency and
 * development level.
 */
export const TERMINAL_PATHWAY_STATUSES: readonly PathwayStatus[] = [
  'improvement_demonstrated',
  'further_development_required',
  'declined',
  'withdrawn',
  'superseded',
];

/** Appendix A: a pathway status counts as open when it is not terminal (D-8). */
export function isOpenPathwayStatus(status: PathwayStatus): boolean {
  return !TERMINAL_PATHWAY_STATUSES.includes(status);
}
